import React, { useEffect, useState } from 'react';
import api, { BASE as API_BASE } from '../../services/api';
import ProjectNameInput from './ProjectNameInput';
import DemographicTagEditor from './DemographicTagEditor';
import FileDropUploader from './FileDropUploader';

export default function ProjectSetup({ onCreated }) {
  const [name, setName] = useState('');
  const [goals, setGoals] = useState('Awareness');
  const [demoInput, setDemoInput] = useState('');
  const [demographics, setDemographics] = useState(['18-34, US']);
  const [busy, setBusy] = useState(false);
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState('');
  const [dup, setDup] = useState(null);
  const [bubble, setBubble] = useState('');
  const [uploads, setUploads] = useState([]); // { url, type }

  useEffect(() => {
    let mounted = true;
    (async () => {
      const r = await api.listProjects();
      if (mounted && r?.items) setProjects(r.items);
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!name) { setDup(null); setBubble(''); return; }
    const match = (projects || []).find(p => String(p.name || '').toLowerCase() === String(name).toLowerCase());
    setDup(match || null);
    if (match) setBubble(`Add to existing "${match.name}"?`);
    else setBubble(name ? `Adding ${name} to Profile Hub…` : '');
  }, [name, projects]);

  async function handleUpload(file) {
    try {
      const r = await api.uploadFile(file);
      const rel = r?.item?.url || r?.path || r?.url || '';
      const url = rel && rel.startsWith('/uploads/') ? `${API_BASE}${rel}` : rel;
      const type = /\.(png|jpg|jpeg|webp|gif|svg)$/i.test(file.name) ? 'image' : /\.(mp3|wav|m4a|aac)$/i.test(file.name) ? 'audio' : 'file';
      setUploads(prev => [{ url, type }, ...prev]);
    } catch {}
  }

  async function handleFiles(files) {
    const list = Array.isArray(files) ? files : [];
    for (const file of list) {
      // eslint-disable-next-line no-await-in-loop
      await handleUpload(file);
    }
  }

  async function genAiPlaceholders() {
    try {
      // Image placeholder via media image generate
      const img = await api.generateImage({ prompt: `${name || 'Brand'} cover, high-contrast, purple/blue` });
      const url = img?.item?.url || img?.url || '';
      if (url) setUploads(prev => [{ url: url.startsWith('/uploads/') ? `${API_BASE}${url}` : url, type: 'image' }, ...prev]);
    } catch {}
  }

  async function handleCreate() {
    setBusy(true); setError('');
    try {
      const res = await api.createProject({ name, goals, demographics: demographics.join(', ') });
      if (res?.projectId) {
        const refreshed = await api.listProjects();
        if (refreshed?.items) setProjects(refreshed.items);
        if (typeof onCreated === 'function') onCreated(res.projectId);
      } else if (res?.error) {
        setError(String(res.error));
      } else {
        setError('create_failed');
      }
    } catch {
      setError('network_error');
    }
    setBusy(false);
  }

  return (
    <div style={{ border: '1px solid #374151', borderRadius: 12, padding: 16, background: '#0b0b0e', position: 'relative' }}>
      <h3 style={{ marginTop: 0, color: '#e5e7eb' }}>Kickstart Your Project on loudest.ai</h3>
      {error && <div style={{ color: '#fca5a5', marginBottom: 8 }}>{error}</div>}
      {bubble && (
        <div style={{ position: 'absolute', right: 16, top: 12, background: '#1f2937', border: '1px solid #ff5a1f', color: '#e5e7eb', padding: '6px 8px', borderRadius: 6, fontSize: 12, opacity: 0.95 }}>
          {bubble}
        </div>
      )}
      <div style={{ display: 'grid', gap: 8, maxWidth: 700 }}>
        <ProjectNameInput
          value={name}
          onChange={setName}
          placeholder="e.g., Bakery Ad Campaign"
          disabled={busy}
        />
        <select aria-label="Goals" value={goals} onChange={(e)=>setGoals(e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }}>
          <option>Awareness</option>
          <option>Engagement</option>
          <option>Conversion</option>
        </select>
        <DemographicTagEditor
          tags={demographics}
          onChange={setDemographics}
          inputValue={demoInput}
          onInputChange={setDemoInput}
          placeholder="Add tag (e.g., Gen Z, US)"
          disabled={busy}
        />
        <FileDropUploader
          onFiles={handleFiles}
          onGeneratePlaceholder={genAiPlaceholders}
          disabled={busy}
          accept="image/*,audio/*"
          title="Drag & drop images or audio here"
          buttonLabel="Generate AI Placeholder"
        />
        {!!uploads.length && (
          <div style={{ display: 'grid', gap: 8 }}>
            {uploads.map((u, i) => (
              <div key={`${u.url}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ color: '#9ca3af', minWidth: 64 }}>{u.type}</div>
                {u.type === 'image' ? <img src={u.url} alt="upload" style={{ height: 60, borderRadius: 6, border: '1px solid #374151' }} />
                  : u.type === 'audio' ? <audio controls src={u.url} style={{ maxWidth: 200 }} />
                  : <a href={u.url} target="_blank" rel="noreferrer" style={{ color: '#93c5fd' }}>{u.url}</a>}
              </div>
            ))}
          </div>
        )}
        <button disabled={!name || busy} onClick={handleCreate} style={{ background: '#00BBF9', color: '#0b0b0e', border: 0, padding: '10px 12px', borderRadius: 8, cursor: 'pointer' }}>Create Project</button>
      </div>
      {projects?.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ color: '#9ca3af', marginBottom: 6 }}>Your Projects</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {projects.map(p => (
              <div key={p.id} style={{ border: '1px solid #272B33', borderRadius: 8, padding: 8, color: '#e5e7eb' }}>
                <div style={{ fontWeight: 600 }}>{p.name}</div>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>{p.goals || '-'} • {p.demographics || '-'}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
